/**
 * Tape Gallery - Enhanced horizontal scrolling gallery
 * Features: Alternating scroll directions, hover pause, responsive behavior
 * Supports multiple instances per page with proper isolation
 */

// Prevent multiple initializations
if (typeof TapeGallery === "undefined") {
  class TapeGallery {
    constructor(sectionId) {
      this.sectionId = sectionId;
      this.galleryContainer = null;
      this.galleryRows = null;
      this.initialized = false;
      this.observers = [];
      this.eventListeners = [];

      // Initialize the instance
      this.init();
    }

    /**
     * Initialize the tape gallery instance
     */
    init() {
      // Find the specific gallery container for this section
      const sectionElement = document.getElementById(this.sectionId);
      if (!sectionElement) {
        console.warn(`Tape gallery section not found: ${this.sectionId}`);
        return;
      }

      this.galleryContainer = sectionElement.querySelector('.tape-gallery-container');
      if (!this.galleryContainer) {
        console.warn(`Tape gallery container not found for section: ${this.sectionId}`);
        return;
      }

      this.galleryRows = this.galleryContainer.querySelectorAll('.tape-gallery-row');
      if (this.galleryRows.length === 0) {
        console.warn(`No tape gallery rows found for section: ${this.sectionId}`);
        return;
      }

      this.initialized = true;
      this.setupGallery();
      this.handleResize();
      this.setupIntersectionObserver();
    }

    /**
     * Set up gallery rows with alternating scroll directions
     */
    setupGallery() {
      if (!this.initialized) {
        return;
      }
      
      this.galleryRows.forEach((row, index) => {
        const track = row.querySelector('.tape-gallery-track');
        if (!track) return;

        // Set up alternating scroll directions
        const isOddRow = (index + 1) % 2 === 1;
        const scrollDirection = isOddRow ? 'right-to-left' : 'left-to-right';
        row.setAttribute('data-scroll-direction', scrollDirection);

        // Calculate and set animation duration based on content width
        this.setAnimationDuration(track);
      });
    }

    /**
     * Set animation duration based on content width and screen size
     */
    setAnimationDuration(track) {
      // Calculate duration based on content width for consistent speed
      const images = track.querySelectorAll('.tape-gallery-image');
      if (images.length === 0) return;

      let totalWidth = 0;
      images.forEach(image => {
        totalWidth += image.offsetWidth + 16; // 16px gap
      });

      // Get screen width for responsive speed adjustment
      const screenWidth = window.innerWidth;
      
      // Base speed varies by screen size for better UX
      let baseSpeed;
      if (screenWidth < 768) {
        // Mobile: faster base speed
        baseSpeed = 300;
      } else if (screenWidth < 1024) {
        // Tablet: medium speed  
        baseSpeed = 150;
      } else {
        // Desktop: slower base speed
        baseSpeed = 80;
      }
      
      // Calculate duration with screen width factor
      // Smaller screens get proportionally faster animations
      const screenFactor = Math.max(0.5, screenWidth / 1920); // Normalize to 1920px baseline
      const adjustedSpeed = baseSpeed * screenFactor;
      const duration = Math.max(8, totalWidth / adjustedSpeed); // Minimum 8s for mobile
            
      track.style.animationDuration = `${duration}s`;
    }

    /**
     * Set up intersection observer for performance optimization
     */
    setupIntersectionObserver() {
      // Check if IntersectionObserver is supported
      if (!('IntersectionObserver' in window)) {
        return;
      }

      // Pause animations when not in viewport to improve performance
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          const track = entry.target.querySelector('.tape-gallery-track');
          if (!track) return;

          if (entry.isIntersecting) {
            track.style.animationPlayState = 'running';
          } else {
            track.style.animationPlayState = 'paused';
          }
        });
      }, {
        threshold: 0.1,
        rootMargin: '50px 0px'
      });

      this.galleryRows.forEach(row => {
        observer.observe(row);
      });

      // Store observer for cleanup
      this.observers.push(observer);
    }

    /**
     * Handle resize events
     */
    handleResize() {
      let resizeTimer;
      
      const resizeHandler = () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          // Recalculate animation durations on resize
          if (this.initialized) {
            this.galleryRows.forEach(row => {
              const track = row.querySelector('.tape-gallery-track');
              if (track) {
                this.setAnimationDuration(track);
              }
            });
          }
        }, 250);
      };

      window.addEventListener('resize', resizeHandler);

      // Store event listener for cleanup
      this.eventListeners.push(
        { element: window, event: 'resize', handler: resizeHandler }
      );
    }

    /**
     * Cleanup and destroy instance
     */
    destroy() {
      // Clean up intersection observers
      this.observers.forEach(observer => {
        observer.disconnect();
      });
      this.observers = [];

      // Clean up event listeners
      this.eventListeners.forEach(({ element, event, handler }) => {
        element.removeEventListener(event, handler);
      });
      this.eventListeners = [];

      this.initialized = false;
    }
  }

  // Expose to global scope
  window.TapeGallery = TapeGallery;

  /**
   * Tape Gallery Manager
   * Manages multiple TapeGallery instances across the page
   */
  class TapeGalleryManager {
    constructor() {
      this.instances = new Map();
      this.initialized = false;
    }

    /**
     * Initialize all tape galleries found on the page
     */
    initializeAll() {
      if (this.initialized) {
        return;
      }

      // Find all tape gallery sections by looking for the tape-gallery class
      const tapeGalleries = document.querySelectorAll('.tape-gallery');

      tapeGalleries.forEach((gallery) => {
        // Find the parent section element
        const section = gallery.closest("section, [id]");
        if (!section?.id) {
          console.warn("Tape gallery found without proper section ID");
          return;
        }

        const sectionId = section.id;

        // Create instance if not already exists
        if (!this.instances.has(sectionId)) {
          const instance = new TapeGallery(sectionId);
          this.instances.set(sectionId, instance);
        }
      });

      this.initialized = true;
    }

    /**
     * Add new instance (for dynamically added sections)
     */
    addInstance(sectionId) {
      if (!this.instances.has(sectionId)) {
        const instance = new TapeGallery(sectionId);
        this.instances.set(sectionId, instance);
        return instance;
      }
      return this.instances.get(sectionId);
    }

    /**
     * Remove instance (for dynamically removed sections)
     */
    removeInstance(sectionId) {
      const instance = this.instances.get(sectionId);
      if (instance) {
        instance.destroy();
        this.instances.delete(sectionId);
      }
    }
  }

  // Create global manager instance
  window.TapeGalleryManager = TapeGalleryManager;
  window.tapeGalleryManager = new TapeGalleryManager();

  /**
   * Initialize when DOM is ready
   */
  function initializeTapeGalleries() {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => {
        window.tapeGalleryManager.initializeAll();
      });
    } else {
      // DOM already loaded
      window.tapeGalleryManager.initializeAll();
    }
  }

  /**
   * Handle dynamic content changes (theme editor, AJAX, etc.)
   */
  function setupDynamicHandlers() {
    // Shopify theme editor events
    // NOTE: event.target.id gives the full DOM ID (e.g. "shopify-section-...")
    // which matches how instances are keyed. event.detail.sectionId omits the prefix.
    document.addEventListener("shopify:section:load", (event) => {
      const section = event.target;
      if (!section || !section.querySelector(".tape-gallery-container")) return;
      setTimeout(() => {
        window.tapeGalleryManager.addInstance(section.id);
      }, 100);
    });

    document.addEventListener("shopify:section:unload", (event) => {
      const section = event.target;
      if (section) {
        window.tapeGalleryManager.removeInstance(section.id);
      }
    });
  }

  // Initialize everything
  initializeTapeGalleries();
  setupDynamicHandlers();
} // End initialization check 